import React, { useEffect} from 'react'
import {observer} from 'mobx-react-lite'
import {StatusMap, TObservablePage} from '../../types/commerce'
import {Container, Stack} from '../../vanilla'
import {useBasketStore, useOrderStore} from '../../store/hooks/useStore'
import {Layout} from '../../components/layout'
import {Seo} from '../../components/seo'
import {Recommender} from '../../components/recommendations'

import CartItems, {CartItemsSkeleton} from './partials/cart-items'
import EmptyCart from './partials/empty-cart'
import {useCheckoutSummary} from './use-checkout-summary'
import {usePageDataTracking} from '../../analytics/page-tracking'
import { useItemsUnavailable } from '../../store/hooks/useItemsUnavailable'
import { useSlotExpiration } from '../../store/hooks/useSlotExpiration'

const CartPage: TObservablePage = observer(() => {
  const {status, hasItems, basket} = useBasketStore()
  const {isRequestProcessing: isOrderFetching} = useOrderStore()
  const {title, checkoutButtonText} = useCheckoutSummary()
  const {handleEditReservationExpired} = useSlotExpiration()
  const recommenderIds =
    basket?.productItems?.map((p) => p.productId)?.filter((id): id is string => Boolean(id)) || []

  const someItemsUnavailable = useItemsUnavailable()

  const sendPageData = usePageDataTracking();
  const pageType = 'cart';
  const pageCategory = 'Cart';

  useEffect(() => {
    sendPageData(pageType, pageCategory)
  }, [])
  
  useEffect(() => {
    handleEditReservationExpired()
  })

  return (
    <Layout>
      <Seo title="My Basket | Iceland Foods" noIndex />
      <Container paddingY={['12px', '48px']} paddingX={['0px', '16px']}>
        <Stack spacing="60px">
          {status !== StatusMap.INIT || isOrderFetching ? (
            hasItems || someItemsUnavailable ? (
              <CartItems title={title} checkoutButtonText={checkoutButtonText} />
            ) : (
              <EmptyCart />
            )
          ) : (
            <CartItemsSkeleton />
          )}
          {recommenderIds.length ? <CartRecommendation recommenderIds={recommenderIds} /> : null}
        </Stack>
      </Container>
    </Layout>
  )
})

const CartRecommendation = React.memo(
  ({recommenderIds}: {recommenderIds: string[]}) => {
    return (
      <Recommender
        name="You may also like"
        recommender="product-to-product"
        containerProps={{paddingX: '0px'}}
        productId={recommenderIds}
      />
    )
  },
  (prevProps, nextProps) => {
    const prevIds = [...prevProps.recommenderIds].sort()
    const nextIds = [...nextProps.recommenderIds].sort()

    return prevIds.toString() === nextIds.toString()
  }
)

CartPage.getTemplateName = () => 'cart-page'

export default CartPage
